import type { ColumnOrderState, ColumnPinningState } from '@tanstack/react-table';

export type ColumnSizingState = Record<string, number>;
export type ColumnVisibilityState = Record<string, boolean>;

export type ColumnState = {
  columnVisibility: ColumnVisibilityState;
  columnOrder: ColumnOrderState;
  columnPinning: ColumnPinningState;
  columnSizing: ColumnSizingState;
};

const LS_SUFFIX_VISIBILITY = '-tableColumnVisibility';
const LS_SUFFIX_ORDER = '-tableColumnOrder';
const LS_SUFFIX_PINNED = '-tableColumnPinned';
const LS_SUFFIX_SIZING = '-tableColumnSizing';

/**
 * Reads column state for a named table from localStorage.
 * Returns null for any key that is not present or unparseable.
 */
export const readColumnStateFromLocalStorage = (tableName: string): ColumnState => {
  const read = <T>(suffix: string, fallback: T): T => {
    try {
      const raw = localStorage.getItem(`${tableName}${suffix}`);
      if (raw == null) return fallback;
      const parsed: T = JSON.parse(raw);
      return parsed ?? fallback;
    } catch {
      return fallback;
    }
  };

  return {
    columnVisibility: read<ColumnVisibilityState>(LS_SUFFIX_VISIBILITY, {}),
    columnOrder: read<ColumnOrderState>(LS_SUFFIX_ORDER, []),
    columnPinning: read<ColumnPinningState>(LS_SUFFIX_PINNED, { left: [], right: [] }),
    columnSizing: read<ColumnSizingState>(LS_SUFFIX_SIZING, {}),
  };
};

/**
 * Writes column state for a named table back to localStorage.
 * Uses setItem directly so callers don't need React state — useLocalStorageState
 * will pick up the new values on the next render cycle.
 */
export const writeColumnStateToLocalStorage = (tableName: string, state: ColumnState): void => {
  try {
    localStorage.setItem(
      `${tableName}${LS_SUFFIX_VISIBILITY}`,
      JSON.stringify(state.columnVisibility)
    );
    localStorage.setItem(`${tableName}${LS_SUFFIX_ORDER}`, JSON.stringify(state.columnOrder));
    localStorage.setItem(`${tableName}${LS_SUFFIX_PINNED}`, JSON.stringify(state.columnPinning));
    localStorage.setItem(`${tableName}${LS_SUFFIX_SIZING}`, JSON.stringify(state.columnSizing));
  } catch {
    // localStorage may be unavailable (private browsing, storage quota exceeded, etc.)
  }
};

/**
 * Serializes column state to a compact pipe/semicolon string.
 *
 * Format per column segment: <id>|<visible>|<width>|<pin>
 *   - visible: 1 or 0
 *   - width: integer px, or empty for default
 *   - pin: l (left), r (right), or empty
 *
 * Column order in the string IS the column order — every column in columnOrder is
 * always emitted so that positional order is preserved even when a column has no
 * other non-default settings. Trailing default fields are omitted per segment to
 * keep the string short (e.g. a reordered-but-otherwise-default column is just "brand").
 *
 * Example: "name||l;brand|1|180|;companyContactPerson|0|240|r;supplier"
 *   - name: visible (default), default width, pinned left
 *   - brand: hidden, 180px, unpinned
 *   - companyContactPerson: hidden, 240px, pinned right
 *   - supplier: all defaults, present only to preserve order
 */
export const serializeTableSettings = (state: ColumnState): string => {
  const { columnVisibility, columnOrder, columnPinning, columnSizing } = state;

  const pinnedLeft = new Set(columnPinning.left ?? []);
  const pinnedRight = new Set(columnPinning.right ?? []);

  // Columns in columnOrder come first (preserving position).
  // Any columns that have an explicit visibility entry but are absent from columnOrder
  // (e.g. custom field columns loaded asynchronously after the order was saved) are
  // appended so that their visibility changes are captured in the serialized string.
  const orderedSet = new Set(columnOrder);
  const allIds = [
    ...columnOrder,
    ...Object.keys(columnVisibility).filter(id => !orderedSet.has(id)),
  ];

  const segments: string[] = [];

  for (const id of allIds) {
    const visible = columnVisibility[id] !== false;
    const width = columnSizing[id];
    const pin = pinnedLeft.has(id) ? 'l' : pinnedRight.has(id) ? 'r' : '';

    const widthStr = width != null ? String(width) : '';
    const visibleStr = visible ? '1' : '0';

    // Build the segment and strip trailing default fields to save space.
    // We always emit the id so that column order is preserved in the string.
    if (pin !== '') {
      segments.push(`${id}|${visibleStr}|${widthStr}|${pin}`);
    } else if (widthStr !== '') {
      segments.push(`${id}|${visibleStr}|${widthStr}`);
    } else if (!visible) {
      segments.push(`${id}|${visibleStr}`);
    } else {
      // All fields are default — emit id only to preserve positional order
      segments.push(id);
    }
  }

  return segments.join(';');
};

/**
 * Deserializes a compact pipe/semicolon column settings string back to ColumnState.
 * Columns not present in the string get their defaults (visible, no custom width, unpinned).
 * The order of segments in the string becomes the columnOrder.
 */
export const deserializeTableSettings = (str: string): ColumnState => {
  const columnVisibility: ColumnVisibilityState = {};
  const columnOrder: ColumnOrderState = [];
  const pinnedLeft: string[] = [];
  const pinnedRight: string[] = [];
  const columnSizing: ColumnSizingState = {};

  if (!str) {
    return {
      columnVisibility,
      columnOrder,
      columnPinning: { left: pinnedLeft, right: pinnedRight },
      columnSizing,
    };
  }

  for (const segment of str.split(';')) {
    const parts = segment.split('|');
    const [id, visibleStr, widthStr, pin] = parts;
    if (!id) continue;

    columnOrder.push(id);

    // Always record an explicit visibility entry (true/false) for any column the
    // view tracked. This makes the saved view authoritative against late-running
    // setters like useInitialHiddenColumns, which only hides columns whose
    // visibility is `undefined`.
    columnVisibility[id] = visibleStr !== '0';

    if (widthStr != null && widthStr !== '') {
      const width = parseInt(widthStr, 10);
      if (!isNaN(width)) {
        columnSizing[id] = width;
      }
    }

    if (pin === 'l') {
      pinnedLeft.push(id);
    } else if (pin === 'r') {
      pinnedRight.push(id);
    }
  }

  return {
    columnVisibility,
    columnOrder,
    columnPinning: { left: pinnedLeft, right: pinnedRight },
    columnSizing,
  };
};
